
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { GitBranch, User, Lock } from 'lucide-react';
import { Paper, PaperAuthor } from '@/types/paper';
import { User as UserType } from '@/types/user';
import { decodeHtmlEntities } from '@/lib/utils/decodeHtmlEntities';
import { applyLatexAccents } from '@/utils/tex-parser';

interface PaperCardHeaderProps {
  paper: Paper;
}

const cleanMetaText = (value?: string | null): string => {
  if (!value) return '';
  try {
    return applyLatexAccents(decodeHtmlEntities(value)).trim();
  } catch {
    return value;
  }
};


const PaperCardHeader: React.FC<PaperCardHeaderProps> = ({ paper }) => {
  // Use pre-enriched authors from paper data (already fetched by getAllPapers)
  const authors = paper.authors || [];
  
  // Always use the first author in the list, regardless of platform status
  const primaryAuthor = React.useMemo(() => {
    if (authors && authors.length > 0) {
      // Return the first author in the list
      return authors[0];
    }
    
    return null;
  }, [authors]);
  
  // Calculate if the paper is recent (less than 7 days old)
  const isRecent = new Date().getTime() - new Date(paper.updatedAt).getTime() < 7 * 24 * 60 * 60 * 1000;
  
  // Has multiple versions
  const hasMultipleVersions = !!paper.currentVersion && paper.currentVersion !== '1.0' && paper.currentVersion !== '1.0.0' && paper.currentVersion !== '1';
  
  // Get additional authors count
  const additionalAuthorsCount = authors ? Math.max(0, authors.length - 1) : 0;
  
  // Helper function to check if the author is a platform user
  const isPlatformUser = (author: PaperAuthor | UserType | null): boolean => {
    if (!author) return false;
    return 'isPlatformUser' in author ? author.isPlatformUser : false;
  };
  
  return (
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Avatar className="h-8 w-8 md:h-9 md:w-9 border border-border flex-shrink-0">
          {isPlatformUser(primaryAuthor) ? (
            <AvatarFallback className="bg-primary/10 text-xs">
              {primaryAuthor?.name?.charAt(0) || 'A'}
            </AvatarFallback>
          ) : (
            <AvatarFallback className="text-xs">
              <User className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
            </AvatarFallback>
          )}
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-xs md:text-sm font-medium leading-tight truncate">{cleanMetaText(primaryAuthor?.name)}</p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className="truncate">{cleanMetaText(primaryAuthor?.institution)}</span>
            {additionalAuthorsCount > 0 && (
              <span className="whitespace-nowrap">
                +{additionalAuthorsCount}
              </span>
            )}
          </div>
        </div>

      </div>
      
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {paper.visibility === 'private' && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 text-[9px] md:text-[10px] px-1.5 py-0.5 flex items-center gap-0.5">
            <Lock className="h-2.5 w-2.5 md:h-3 md:w-3" />
            <span>Private</span>
          </Badge>
        )}
        
        {isRecent && (
          <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200 text-[9px] md:text-[10px] px-1.5 py-0.5">
            New
          </Badge>
        )}
        
        {hasMultipleVersions && (
          <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-[9px] md:text-[10px] px-1.5 py-0.5 flex items-center gap-0.5">
            <GitBranch className="h-2.5 w-2.5 md:h-3 md:w-3" />
            <span>v{paper.currentVersion}</span>
          </Badge>
        )}
      </div>
    </div>
  );
};

export default PaperCardHeader;
